import type { IFileItem } from '../models/FileItem';
import type { IFolderItem } from '../models/FolderItem';
import type { IHealthReport, IIssue, IRuleOptions } from '../models/Issue';
import type { IOrganizationPlan, IPlannedChange } from '../models/Plan';
import type { IScanResult } from '../models/ScanResult';
import { getPoorFilenameReason, getSystemFileSource } from '../rules/FilenameRule';
import { deepestBelow, isCatchAllFolderName, normalizeFolderName } from '../rules/FolderRules';
import { filePath, folderLabel } from '../rules/ruleUtils';
import { cleanUpFileName } from './names';

export const QUARANTINE_FOLDER: string = '_Quarantine';

/**
 * Fixed confidence per deterministic rule. These express how safe the change is,
 * not a model probability; AI suggestions (Phase 5) will carry their own scores.
 */
export const RULE_CONFIDENCE = {
  systemFile: 0.98,
  exactDuplicate: 0.95,
  emptyFile: 0.9,
  emptyFolder: 0.85,
  cleanRename: 0.8,
  mergeFolders: 0.75,
  flattenNesting: 0.7
};

/** Top levels (categories and the level under them) are never removed when flattening. */
const MIN_FLATTEN_DEPTH: number = 3;
/** Folder names that carry meaning a person would miss, e.g. years. */
const MEANINGFUL_FOLDER_NAME: RegExp = /(^|\D)(19|20)\d{2}(\D|$)/;

/** Folder names that add little context; flattening removes these first. */
const GENERIC_FOLDER_NAME: RegExp =
  /^(assets?|drafts?|archive[sd]?|backups?|final|finals|old|old stuff|stuff|misc|miscellaneous|temp|tmp|other|files?|docs?|documents?|new folder|subfolder|folder|really_?final)(\s*\(\d+\))?$/i;

/**
 * One quarantine folder per scan, e.g. '2026-09-27 20.11.47 UTC'. Separate scans never
 * share a folder, so a later cleanup can't collide with folders an earlier one created;
 * re-applying the same plan reuses its folder, which keeps re-runs safe.
 */
export function quarantineStamp(isoTimestamp: string): string {
  return `${isoTimestamp.substring(0, 10)} ${isoTimestamp.substring(11, 19).replace(/:/g, '.')} UTC`;
}

function isInCatchAll(scan: IScanResult, folderId: string | undefined): boolean {
  let id: string | undefined = folderId;
  while (id && id !== scan.rootId) {
    const folder: IFolderItem = scan.folders[id];
    if (isCatchAllFolderName(folder.name)) {
      return true;
    }
    id = folder.parentId;
  }
  return false;
}

/** Lower is better: the copy to keep has a good name, sits outside catch-all folders, and is shallow. */
function keepPenalty(scan: IScanResult, file: IFileItem): number {
  return (getPoorFilenameReason(file) ? 4 : 0) +
    (isInCatchAll(scan, file.parentId) ? 3 : 0) +
    scan.folders[file.parentId].depth * 0.1;
}

function pickCopyToKeep(scan: IScanResult, files: IFileItem[]): IFileItem {
  return files.slice().sort((a, b) =>
    keepPenalty(scan, a) - keepPenalty(scan, b) ||
    a.createdDateTime.localeCompare(b.createdDateTime) ||
    filePath(a).localeCompare(filePath(b))
  )[0];
}

function quarantineFile(file: IFileItem, reason: string, confidence: number, issue: IIssue): IPlannedChange {
  return {
    id: `quarantineFile:${file.id}`,
    type: 'quarantineFile',
    itemKind: 'file',
    targetId: file.id,
    needsInput: false,
    reason,
    confidence,
    issueIds: [issue.id]
  };
}

/** Files beneath a folder, ignoring system files so a stray Thumbs.db can't decide a merge. */
function realFileCount(scan: IScanResult, folder: IFolderItem): number {
  let count: number = folder.fileIds.filter(id => !getSystemFileSource(scan.files[id])).length;
  for (const childId of folder.childFolderIds) {
    count += realFileCount(scan, scan.folders[childId]);
  }
  return count;
}

/** Folder a similar-folder group should merge into: most content, then the clearest, fullest name. */
function pickMergeTarget(scan: IScanResult, folders: IFolderItem[]): IFolderItem {
  const hasFiller = (f: IFolderItem): number => (/\b(documents?|docs?|files?|folders?)\b/i.test(f.name) ? 1 : 0);
  return folders.slice().sort((a, b) =>
    (realFileCount(scan, b) - realFileCount(scan, a)) ||
    (hasFiller(a) - hasFiller(b)) ||
    (b.name.length - a.name.length) ||
    a.name.localeCompare(b.name)
  )[0];
}

/** A folder that holds nothing but one subfolder: removing it loses no content. */
function isPassThrough(folder: IFolderItem): boolean {
  return folder.fileIds.length === 0 && folder.childFolderIds.length === 1;
}

/** Folders from the library root down to the deepest folder under `folder`. */
function deepestChain(scan: IScanResult, folder: IFolderItem): IFolderItem[] {
  const chain: IFolderItem[] = [];
  for (let f: IFolderItem | undefined = folder; f && f.id !== scan.rootId; f = f.parentId ? scan.folders[f.parentId] : undefined) {
    chain.unshift(f);
  }
  let current: IFolderItem = folder;
  while (current.childFolderIds.length > 0) {
    current = current.childFolderIds
      .map(id => scan.folders[id])
      .sort((a, b) => deepestBelow(scan, b) - deepestBelow(scan, a) || a.name.localeCompare(b.name))[0];
    chain.push(current);
  }
  return chain;
}

/**
 * Builds the organization plan from the health report. Deterministic: the same scan and
 * settings always produce the same plan. Nothing here touches SharePoint.
 *
 * Catch-all folders, possible duplicates and long paths produce no automatic changes —
 * deciding where those files belong needs a person (or AI in Phase 5).
 */
export function generatePlan(scan: IScanResult, report: IHealthReport, options: IRuleOptions): IOrganizationPlan {
  const changes: IPlannedChange[] = [];
  const quarantinedFileIds: { [id: string]: boolean } = {};

  const addQuarantine = (change: IPlannedChange): void => {
    if (!quarantinedFileIds[change.targetId]) {
      quarantinedFileIds[change.targetId] = true;
      changes.push(change);
    }
  };

  for (const issue of report.byCategory.systemFiles) {
    const file: IFileItem = scan.files[issue.fileIds[0]];
    addQuarantine(quarantineFile(file,
      `${getSystemFileSource(file)} — created automatically and not needed in SharePoint.`,
      RULE_CONFIDENCE.systemFile, issue));
  }

  for (const issue of report.byCategory.duplicates) {
    const copies: IFileItem[] = issue.fileIds.map(id => scan.files[id]);
    const keep: IFileItem = pickCopyToKeep(scan, copies);
    for (const copy of copies) {
      if (copy.id !== keep.id) {
        addQuarantine(quarantineFile(copy,
          `Identical to "${filePath(keep)}", which is kept.`,
          RULE_CONFIDENCE.exactDuplicate, issue));
      }
    }
  }

  for (const issue of report.byCategory.emptyFiles) {
    addQuarantine(quarantineFile(scan.files[issue.fileIds[0]],
      'The file is empty (0 bytes).', RULE_CONFIDENCE.emptyFile, issue));
  }

  for (const issue of report.byCategory.emptyFolders) {
    const folder: IFolderItem = scan.folders[issue.folderIds[0]];
    changes.push({
      id: `quarantineFolder:${folder.id}`,
      type: 'quarantineFolder',
      itemKind: 'folder',
      targetId: folder.id,
      needsInput: false,
      reason: folder.childFolderIds.length > 0
        ? 'The folder and its subfolders contain no files.'
        : 'The folder has no files or subfolders.',
      confidence: RULE_CONFIDENCE.emptyFolder,
      issueIds: [issue.id]
    });
  }

  for (const issue of report.byCategory.similarFolders) {
    // Empty folders are already quarantined; merging them would be noise.
    const group: IFolderItem[] = issue.folderIds.map(id => scan.folders[id])
      .filter(f => f.fileIds.length > 0 || f.childFolderIds.length > 0);
    if (group.length < 2) {
      continue;
    }
    const target: IFolderItem = pickMergeTarget(scan, group);
    const sources: IFolderItem[] = group.filter(f => f.id !== target.id);
    changes.push({
      id: `mergeFolders:${target.id}`,
      type: 'mergeFolders',
      itemKind: 'folder',
      targetId: target.id,
      sourceFolderIds: sources.map(f => f.id),
      candidateFolderIds: group.map(f => f.id),
      needsInput: false,
      reason: `These folders appear to hold the same kind of content ("${normalizeFolderName(target.name)}"). ` +
        `Consolidating them into "${target.name}" keeps related documents in one place.`,
      confidence: RULE_CONFIDENCE.mergeFolders,
      issueIds: [issue.id]
    });
  }

  // Flatten over-nested branches by removing pass-through folders along the deepest path,
  // so every file keeps its surrounding context. Branches with nothing safe to remove stay
  // in the health report for a person (or, later, AI) to restructure.
  for (const issue of report.byCategory.deepNesting) {
    const folder: IFolderItem = scan.folders[issue.folderIds[0]];
    const chain: IFolderItem[] = deepestChain(scan, folder);
    const deepest: IFolderItem = chain[chain.length - 1];
    const excess: number = deepest.depth - options.maxDepth;
    // Remove generic folders first, then the deepest ones: names near the top of a branch
    // (a client, a project) usually carry the context people navigate by.
    const collapse: IFolderItem[] = chain
      .filter(f => f.depth >= MIN_FLATTEN_DEPTH && f.id !== deepest.id && isPassThrough(f) &&
        !MEANINGFUL_FOLDER_NAME.test(f.name) && !scan.protectedFolders[f.id])
      .sort((a, b) => (Number(GENERIC_FOLDER_NAME.test(b.name)) - Number(GENERIC_FOLDER_NAME.test(a.name))) || (b.depth - a.depth))
      .slice(0, excess)
      .sort((a, b) => a.depth - b.depth); // Applied shallowest first.
    if (collapse.length === 0) {
      continue;
    }
    const removed: { [id: string]: boolean } = {};
    for (const f of collapse) {
      removed[f.id] = true;
    }
    const flattenTo: string = chain.filter(f => !removed[f.id]).map(f => f.name).join('/');
    const remaining: number = deepest.depth - collapse.length;
    changes.push({
      id: `flattenFolders:${folder.id}`,
      type: 'flattenFolders',
      itemKind: 'folder',
      targetId: folder.id,
      collapseFolderIds: collapse.map(f => f.id),
      flattenFrom: deepest.path,
      flattenTo,
      needsInput: false,
      reason: `"${folderLabel(folder)}" goes ${deepest.depth} levels deep. ` +
        `${collapse.length === 1 ? 'The folder' : 'The folders'} ${collapse.map(f => `"${f.name}"`).join(', ')} ` +
        `${collapse.length === 1 ? 'holds' : 'each hold'} nothing but one other folder, so removing ` +
        `${collapse.length === 1 ? 'it' : 'them'} shortens the path without separating any file from its project or context. ` +
        (remaining > options.maxDepth
          ? `It will still be ${remaining} levels deep; the rest needs a person to restructure.`
          : `That brings it within ${options.maxDepth} levels.`) +
        ' Removed folders are empty afterwards and go to quarantine.',
      confidence: RULE_CONFIDENCE.flattenNesting,
      issueIds: [issue.id]
    });
  }

  const possibleDuplicateOf: { [fileId: string]: IIssue } = {};
  for (const issue of report.byCategory.possibleDuplicates) {
    for (const id of issue.fileIds) {
      possibleDuplicateOf[id] = issue;
    }
  }

  for (const issue of report.byCategory.poorFilenames) {
    const file: IFileItem = scan.files[issue.fileIds[0]];
    if (quarantinedFileIds[file.id]) {
      continue;
    }
    const similar: IIssue | undefined = possibleDuplicateOf[file.id];
    const siblingNames: { [name: string]: boolean } = {};
    for (const id of scan.folders[file.parentId].fileIds) {
      if (id !== file.id) {
        siblingNames[scan.files[id].name.toLowerCase()] = true;
      }
    }
    const cleaned: string | undefined = similar ? undefined : cleanUpFileName(file);
    const clash: boolean = !!cleaned && siblingNames[cleaned.toLowerCase()];
    const suggestedName: string | undefined = cleaned && !clash ? cleaned : undefined;

    let reason: string = getPoorFilenameReason(file) ?? 'Poor filename';
    if (suggestedName) {
      reason += '. Removing copy markers and version words gives a cleaner name.';
    } else if (similar) {
      reason += '. It looks like another version of ' +
        similar.fileIds.filter(id => id !== file.id).map(id => `"${scan.files[id].name}"`).join(', ') +
        ' — decide which to keep, then give it a clear name.';
    } else if (clash) {
      reason += `. The cleaned-up name "${cleaned}" is already used in this folder — choose a different name.`;
    } else {
      reason += '. Type a name that says what the document is.';
    }

    changes.push({
      id: `renameFile:${file.id}`,
      type: 'renameFile',
      itemKind: 'file',
      targetId: file.id,
      suggestedName,
      needsInput: !suggestedName,
      reason,
      confidence: suggestedName ? RULE_CONFIDENCE.cleanRename : 0,
      issueIds: [issue.id]
    });
  }

  return {
    changes,
    quarantinePath: `${QUARANTINE_FOLDER}/${quarantineStamp(scan.scannedAt)}`
  };
}
